import { WEB_TELEMETRY_MAX_EVENTS_PER_BATCH, type WebTelemetryEvent } from '@doss/common-all'
import config from '~/common/config/config'
import { getUserBearerToken } from '~/common/lib/firebase'

const FLUSH_INTERVAL_MS = 15_000
const MAX_QUEUE_SIZE = 200

let queue: WebTelemetryEvent[] = []
let backgroundFlushInstalled = false
let flushTimer: ReturnType<typeof setInterval> | undefined
let visibilityFlushListener: (() => void) | undefined
let pendingBearerToken: Promise<string> | undefined
let flushPromise: Promise<void> | undefined
let identity: string | undefined
let identityVersion = 0

const prepareBearerToken = (): Promise<string> => {
  if (pendingBearerToken !== undefined) {
    return pendingBearerToken
  }

  const bearerTokenPromise = getUserBearerToken()
  pendingBearerToken = bearerTokenPromise
  void bearerTokenPromise.catch(() => {
    if (pendingBearerToken === bearerTokenPromise) {
      pendingBearerToken = undefined
    }
  })
  return bearerTokenPromise
}

const drainQueue = async (newestFirst = false): Promise<void> => {
  while (queue.length > 0) {
    const events = newestFirst
      ? queue.splice(-WEB_TELEMETRY_MAX_EVENTS_PER_BATCH)
      : queue.splice(0, WEB_TELEMETRY_MAX_EVENTS_PER_BATCH)
    const batchIdentityVersion = identityVersion
    const bearerTokenPromise = prepareBearerToken()
    pendingBearerToken = undefined

    try {
      const bearerToken = await bearerTokenPromise
      if (batchIdentityVersion !== identityVersion) {
        continue
      }
      // keepalive lets a final flush survive tab close/navigation; WorkOS auth rides the session
      // cookie (credentials: 'include'), Firebase auth rides the bearer header — same policy as
      // the GraphQL client.
      const response = await fetch(`${config.apiUrl}/telemetry/web-perf`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(bearerToken ? { authorization: bearerToken } : {}),
        },
        credentials: 'include',
        keepalive: true,
        body: JSON.stringify({ events }),
      })
      if (!response.ok) {
        throw new Error(`Telemetry request failed with status ${response.status}`)
      }
    } catch (error) {
      // Telemetry is fail-silent by design: drop the batch, never retry, never throw.
      // oxlint-disable-next-line no-console
      console.debug('[perf-telemetry] Failed to send batch:', error)
    }
  }
}

const flush = (): Promise<void> => {
  flushPromise ??= drainQueue().finally(() => {
    flushPromise = undefined
  })
  return flushPromise
}

const ensureBackgroundFlush = (): void => {
  if (backgroundFlushInstalled) {
    return
  }
  backgroundFlushInstalled = true
  flushTimer = setInterval(() => {
    void flush()
  }, FLUSH_INTERVAL_MS)
  // Flush pending events when the tab is hidden so the last page view isn't lost on close
  visibilityFlushListener = () => {
    if (document.visibilityState === 'hidden') {
      void flush()
    }
  }
  document.addEventListener('visibilitychange', visibilityFlushListener)
}

export const prepareWebPerfBeacon = (): void => {
  if (!config.perfTelemetryEnabled || identity === undefined) {
    return
  }

  // A tracked route starts a new measurement lifecycle. Discard any resolved promise retained by
  // an earlier canceled load so a later event cannot reuse an expired bearer token.
  pendingBearerToken = undefined
  void prepareBearerToken().catch(() => undefined)
}

export const enqueueWebTelemetryEvent = (event: WebTelemetryEvent): void => {
  if (!config.perfTelemetryEnabled || identity === undefined) {
    return
  }
  ensureBackgroundFlush()

  queue.push(event)
  void prepareBearerToken().catch(() => undefined)
  if (queue.length > MAX_QUEUE_SIZE) {
    queue = queue.slice(queue.length - MAX_QUEUE_SIZE)
  }
  if (queue.length >= WEB_TELEMETRY_MAX_EVENTS_PER_BATCH || document.visibilityState === 'hidden') {
    void flush()
  }
}

// Unload-path flush (pagehide). pagehide handlers can run while document.visibilityState still
// reads 'visible', so an event enqueued from one bypasses the hidden-state flush triggers above —
// callers flush explicitly instead. Deliberately bypasses the flush() in-flight dedupe too: a
// drain stuck awaiting an earlier fetch may never resume once the document tears down, and the
// final events must not wait behind it. Drain newest-first as well, so the pagehide event starts
// sending before older queued batches. Concurrent drains are safe — batches are spliced off the
// shared queue synchronously, so no event can be sent twice — and the keepalive fetch survives
// document teardown once started.
export const flushWebPerfEvents = (): Promise<void> => drainQueue(true)

export const setPerfBeaconIdentity = (nextIdentity: string | undefined): void => {
  if (identity === nextIdentity) {
    return
  }
  identity = nextIdentity
  identityVersion += 1
  queue = []
  pendingBearerToken = undefined
}

export const resetPerfBeaconForTest = (): void => {
  queue = []
  identity = undefined
  identityVersion += 1
  backgroundFlushInstalled = false
  pendingBearerToken = undefined
  // A drain left awaiting a never-settled fetch by a failed test must not dedupe away the next
  // test's flushes.
  flushPromise = undefined
  if (flushTimer !== undefined) {
    clearInterval(flushTimer)
    flushTimer = undefined
  }
  if (visibilityFlushListener !== undefined) {
    document.removeEventListener('visibilitychange', visibilityFlushListener)
    visibilityFlushListener = undefined
  }
}
